import { I18n } from "@assets/strings";
import { config } from "@core/config/config";
import { Environment } from "@core/config/environment";
import { Path } from "@core/routing/path";
import { useHistory } from "@core/routing/useHistory";
import { useServices } from "@core/servicesContext";
import { completeProfileProgress, missingCompleteProfileSteps } from "@domain/currentProfile/completeProfilePrompt";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Profile } from "@domain/user/profile";
import { fullName } from "@domain/user/utils";
import { useAuthModalBlocker } from "@ui/components/auth/useAuthModalBlocker";
import { SecondaryButton } from "@ui/components/buttons/secondaryButton";
import { CategoriesView } from "@ui/components/categoriesView";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { Primary } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Tertiary } from "@ui/components/containers/textBlocks";
import {
	AssetManagerIcon,
	CalendarIcon,
	CategoryIcon,
	ChevronDownIcon,
	EditIcon,
	LogoutIcon,
	PictureIcon,
} from "@ui/components/icons";
import { Bloc, row, stack, Stack } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { MenuRow } from "@ui/components/menus/menuRow";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { ScrollBehavior } from "@ui/components/navigation/scrollToTop";
import { CategoriesEditionModal } from "@ui/components/profile/categoriesEditionModal";
import { CompleteProfileModal } from "@ui/components/profile/completeProfileModal";
import { ContactButton } from "@ui/components/profile/contactButton";
import { CropProfilePicture } from "@ui/components/profile/cropProfilePicture";
import { FollowButton } from "@ui/components/profile/followButton";
import { OpenModalPicture } from "@ui/components/profile/openModalPicture";
import { ProfileBanner } from "@ui/components/profile/profileBanner";
import { ProfileEditionModal } from "@ui/components/profile/profileEditionModal";
import { ProfileLocation } from "@ui/components/profile/profileLocation";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { useModalTools } from "@ui/modals/modalContext";
import { theme } from "@ui/styles/theme";
import { desktop, gap, mobile } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { getBanner } from "@utils/banner";
import { useIsMobile } from "@utils/hooks";
import { readableCount } from "@utils/readableCount";
import React from "react";
import { Link } from "react-router";
import styled, { css } from "styled-components";
import { FollowersModal } from "./followersModal";
import { FollowingModal } from "./followingModal";
import { InvitationButton } from "./invitationButton";
import { ProfileDescriptionEditor } from "./profileDescriptionEditor";

interface ProfileCardProps extends React.HTMLAttributes<HTMLDivElement> {
	profile: Profile;
	hideDetails?: boolean;
}

export const ProfileCard: React.FC<ProfileCardProps> = ({ profile, hideDetails, ...htmlProps }) => {
	const { currentProfileService } = useServices();

	const currentProfile = useCurrentProfile();

	const {
		id,
		avatar,
		banner,
		description,
		presentation,
		categories,
		followingMeSince,
		followerCount,
		followeeCount,
		contentCount,
		countryCode,
		memberSince,
		slug,
	} = profile;

	const isMe = currentProfile?.id === id;
	const missingProfileSteps = isMe && currentProfile ? missingCompleteProfileSteps(currentProfile) : [];
	const profileProgress = currentProfile ? completeProfileProgress(currentProfile) : 0;
	const { createModal } = useModalTools();
	const isMobile = useIsMobile();

	const openCompleteProfile = (): void => {
		createModal((close) => <CompleteProfileModal close={close} />);
	};

	const openEdition = (): void => {
		if (currentProfile) {
			createModal((close) => <ProfileEditionModal initialProfile={currentProfile} close={close} />);
		}
	};

	const openCorpProfilePicture = (): void => {
		if (!hideDetails && currentProfile) {
			createModal((close) => <CropProfilePicture initialProfile={currentProfile} close={close} />);
		}
	};
	const openModalProfilPicture = (): void => {
		if (!isMe && !hideDetails && avatar) {
			createModal((close) => <OpenModalPicture profileSlug={slug} imageProfile={avatar} close={close} />, {
				closeOnBackdropClick: true,
			});
		}
	};

	const blockGuest = useAuthModalBlocker();
	const history = useHistory();
	const { followedByMeSince } = profile;

	return (
		<Container {...htmlProps} hideDetails={hideDetails}>
			<ProfileBanner picture={banner} big={!hideDetails} defaultUrl={!banner ? getBanner(id) : undefined} />
			<ProfilePictureWrapper>
				<Relative>
					<Link to={Path.Profile(profile)}>
						<ProfilePicture
							onClick={openModalProfilPicture}
							alt={I18n.t("seo.profile.picture", { name: fullName(profile) })}
							picture={avatar}
							size="XL"
							shadowed
							hideDetails={!hideDetails}
						/>
					</Link>
					{isMe && !hideDetails && (
						<QuickEdit
							onClick={(e) => {
								e.stopPropagation();
								openCorpProfilePicture();
							}}
						>
							<PictureIcon />
						</QuickEdit>
					)}

					{/* {profile.statusApplication === "PENDING_VALIDATION" && (
						<QuickFilter>
							<img src={require("../../../assets/images/talent.png")} width="95px" height="65px" />
						</QuickFilter>
					)} */}
				</Relative>
				<Bloc style={{ display: "flex", justifyContent: "flex-end" }}>
					{!hideDetails && isMe && (
						<Link to={Path.AssetManager}>
							<SecondaryButton lighter>
								<AssetManagerIcon /> {I18n.t(isMobile ? "asset_manager.title" : "asset_manager.mine.title")}
							</SecondaryButton>
						</Link>
					)}
					{!hideDetails && !isMe && <FollowButton isBig profile={profile} iconMode={isMobile} />}
					{/* <ContactButton currentProfile={currentProfile} isBig profile={profile} iconMode={isMobile} /> */}
					{/* {followedByMeSince && config.features.profile.messaging && (
						<ContactButton currentProfile={currentProfile} isBig profile={profile} iconMode={isMobile} />
					)}  */}
					{followedByMeSince && profile.followingMeSince && config.features.profile.messaging && (
						<ContactButton currentProfile={currentProfile} isBig profile={profile} iconMode={isMobile} />
					)}
					{!hideDetails && isMe && !isMobile && <StyledInvitationButton />}
				</Bloc>
			</ProfilePictureWrapper>

			{followingMeSince && <FollowerInfos>{I18n.t("profile.card.follower")}</FollowerInfos>}
			<Link to={Path.Profile(profile)}>
				<Name smallMargin={!!followingMeSince}>{fullName(profile)}</Name>
			</Link>
			{missingProfileSteps.length > 0 && currentProfile && (
				<CompleteProfileButton type="button" onClick={openCompleteProfile}>
					<CompleteProfileMain>
						<span>{I18n.t("profile.complete.label")}</span>
						<ProgressTrack>
							<ProgressFill
								$ratio={profileProgress}
								role="progressbar"
								aria-valuemin={0}
								aria-valuemax={100}
								aria-valuenow={Math.round(profileProgress * 100)}
							/>
						</ProgressTrack>
					</CompleteProfileMain>
					<ChevronDownIcon style={{ transform: "rotate(-90deg)" }} />
				</CompleteProfileButton>
			)}
			{!!description && <Description secondary={hideDetails}>{description.toString()}</Description>}
			{isMe && !hideDetails && currentProfile && <ProfileDescriptionEditor initialProfile={currentProfile} />}

			<Content hideDetails={hideDetails}>
				<Stack gap={10}>
					{!hideDetails && presentation && presentation.hasText() && (
						<AboutInfo content={presentation} fold contractibleFold foldColor={theme.colors.secondary} />
					)}
					{hideDetails && countryCode && <StyledLocation countryCode={countryCode} />}
					<Popularity>
						<Data
							onClick={
								followerCount > 0
									? () => blockGuest(() => createModal((close) => <FollowersModal close={close} profileId={id} />))
									: undefined
							}
						>
							<div>{readableCount(followerCount)}</div>
							<Tertiary>{I18n.t("profile.card.followers")}</Tertiary>
						</Data>
						<Data
							onClick={
								followeeCount > 0
									? () =>
											blockGuest(() =>
												createModal((close) => (
													<FollowingModal count={readableCount(followeeCount)} close={close} profileId={id} />
												))
											)
									: undefined
							}
						>
							<div>{readableCount(followeeCount)}</div>
							<Tertiary>{I18n.t("profile.card.followees")}</Tertiary>
						</Data>
						{!!contentCount && !hideDetails && (
							<Data
								onClick={
									contentCount > 0
										? () => blockGuest(() => history.push(Path.Profile(profile, "content"), ScrollBehavior.NoScroll))
										: undefined
								}
							>
								<div>{readableCount(contentCount)}</div>
								<Tertiary>
									{I18n.t(contentCount > 1 ? "profile.card.content.more_one" : "profile.card.content.at_most_one")}
								</Tertiary>
							</Data>
						)}
					</Popularity>
				</Stack>
				{!hideDetails && (
					<Stack gap={10} style={{ margin: "auto" }}>
						{countryCode && <StyledLocation countryCode={countryCode} />}
						{memberSince && (
							<MembershipYear>
								<CalendarIcon />
								<Primary>{`${I18n.t("profile.card.member_since")} ${I18n.t(
									`date_month_${memberSince.getMonth()}`
								)} ${memberSince.getFullYear()}`}</Primary>
							</MembershipYear>
						)}
						{!hideDetails && <StyledCategoriesView categories={categories} />}
					</Stack>
				)}
			</Content>
			{!hideDetails && isMe && isMobile && <BottomInvitationButton />}
			{!!currentProfile && (
				<ProfileMenu closeOnSelect blur>
					{isMe ? (
						<MenuContent>
							<MenuRow onClick={openEdition}>
								<IconWrapper>
									<EditIcon />
								</IconWrapper>
								<span>{I18n.t("profile.menu.edit")}</span>
							</MenuRow>
							<MenuRow
								onClick={() =>
									createModal((close) => (
										<CategoriesEditionModal
											initialCategories={currentProfile.categories}
											close={close}
											title={I18n.t("profile.categories.title")}
											saveCategories={(categories) => currentProfileService.saveProfileInfos({ categories })}
										/>
									))
								}
							>
								<IconWrapper mainPath>
									<CategoryIcon />
								</IconWrapper>
								<span>{I18n.t("profile.menu.categories")}</span>
							</MenuRow>
							<MenuRow onClick={() => currentProfileService.logout()}>
								<IconWrapper>
									<LogoutIcon />
								</IconWrapper>
								<span>{I18n.t("logout")}</span>
							</MenuRow>
						</MenuContent>
					) : (
						<SimpleMenuContent
							reportInfos={{
								contentId: profile.id,
								contentType: "USER",
							}}
						/>
					)}
				</ProfileMenu>
				// ici on utilise le one to one chat de getstream
			)}
		</Container>
	);
};

const Container = styled(RoundedBlock).withConfig({
	shouldForwardProp: (prop) => prop !== "hideDetails",
})<{ hideDetails?: boolean }>`
	position: relative;
	padding: 162px 33px 38px;
	@media ${mobile} {
		padding-top: 85px;
	}
	${({ hideDetails }) =>
		hideDetails &&
		css`
			padding: 52px 31px 23px 31px;
		`};
`;

const ProfileMenu = styled(KebabMenu)`
	position: absolute;
	right: 10px;
	top: 10px;
`;

const MenuContent = styled.div`
	${gap(20)}
`;

const IconWrapper = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "mainPath",
})<{ mainPath?: boolean }>`
	flex-shrink: 0;
	border-radius: 9px;
	width: 34px;
	height: 34px;
	background-color: ${({ theme }) => theme.colors.secondaryLight};
	${row(0, "center", "center")};
	${({ mainPath }) =>
		mainPath
			? css`
					> svg .main-path {
						fill: ${({ theme }) => theme.colors.secondary};
					}
				`
			: css`
					> svg path {
						fill: ${({ theme }) => theme.colors.secondary};
					}
				`};
`;

const Content = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "hideDetails",
})<{ hideDetails?: boolean }>`
	position: relative;
	margin-top: 13px;

	> div {
		max-width: 320px;
	}
	img {
		box-shadow: 0 22px 74px 0 rgba(0, 0, 0, 0.3);
	}

	@media ${desktop} {
		${row(0)};

		${({ hideDetails }) =>
			!hideDetails &&
			css`
				> :first-child {
					width: 270px;
					flex-shrink: 0;
				}
			`}
	}

	@media ${mobile} {
		${stack(40)};
	}
`;

const CompleteProfileButton = styled.button`
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	max-width: 320px;
	margin-top: 16px;
	padding: 12px 14px;
	border: 1px solid #d7eee0;
	border-radius: 16px;
	background: #f4fbf7;
	text-align: left;
	cursor: pointer;
	font-family: inherit;

	&:hover {
		border-color: #b7dfc8;
		background: #e7f6ee;
	}

	.main-path {
		fill: #a7b3c2;
	}
`;

const CompleteProfileMain = styled.span`
	flex: 1;
	min-width: 0;

	> span {
		display: block;
		font-size: 13.5px;
		font-weight: 600;
		color: #5b6272;
	}
`;

const ProgressTrack = styled.span`
	display: block;
	height: 6px;
	margin-top: 8px;
	border-radius: 999px;
	background: #e9edf2;
	overflow: hidden;
`;

const ProgressFill = styled.span<{ $ratio: number }>`
	display: block;
	height: 100%;
	width: ${({ $ratio }) => `${Math.max(0, Math.min(1, $ratio)) * 100}%`};
	border-radius: 999px;
	background: linear-gradient(140deg, #2ec46f 0%, #00863d 100%);
`;

const Name = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "smallMargin",
})<{ smallMargin: boolean }>`
	${({ theme }) => theme.textStyles.primaryBold};
	margin-top: ${({ smallMargin }) => (smallMargin ? 15 : 35)}px;
	font-size: 1.25rem;
`;

const AboutInfo = styled(DraftRenderer)`
	color: ${({ theme }) => theme.colors.textSecondary};
	font-size: 0.9375rem;
	word-break: break-word;
`;

const FollowerInfos = styled.div`
	display: table;
	margin-top: 15px;
	border-radius: 4px;
	background-color: ${({ theme }) => theme.colors.primaryLight};
	color: ${({ theme }) => theme.colors.primary};
	font-size: 0.625rem;
	font-weight: bold;
	padding: 4px 5px;
`;

const StyledLocation = styled(ProfileLocation)``;

const Description = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "secondary",
})<{ secondary?: boolean }>`
	${({ theme }) => theme.textStyles.primary};
	color: ${({ theme, secondary }) => (secondary ? theme.colors.textSecondary : theme.colors.textPrimary)};
	word-break: break-word;
	margin-top: 3px;
	max-width: 320px;
`;

const Popularity = styled.div`
	margin-top: 20px;
	${row(30)};

	@media ${mobile} {
		margin-top: 0px;
	}
`;

const Data = styled.div<{ onClick?: () => void }>`
	${({ onClick }) => onClick && "cursor: pointer"};
	${stack(0)}

	> :first-child {
		${({ theme }) => theme.textStyles.title};
		color: ${({ theme }) => theme.colors.secondary};
	}
`;

const MembershipYear = styled.div`
	${row(10, "flex-start", "center")};
	margin-left: 4px;
`;

const StyledCategoriesView = styled(CategoriesView)`
	${row(6, "flex-start", "center")};
	margin-top: 40px;
`;

const QuickEdit = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	border-radius: 50%;
	position: absolute;
	right: -5px;
	bottom: 15px;
	z-index: ${Z.over};
	cursor: pointer;
	padding: 5px;
	background-color: ${({ theme }) => theme.colors.secondary};
	outline: ${Environment.APP_VARIANT === "jaangal" ? "2px solid white" : "none"};
`;

// const QuickFilter = styled(Primary)<{ big?: boolean; color?: string; iconColor?: string }>
// 	z-index: ${Z.over};
// 	position: absolute;
// 	/* right: 10px; */
// 	bottom: 10px;
// 	cursor: pointer;
// 	border-radius: 13px;
// 	display: flex;
// 	align-items: center;
// 	position: absolute;
// 	/* right: 2px; */
// 	bottom: 0px;
// 	top: 73px;
// 	left: 0px;
// 	padding: 5px;

// 	${({ iconColor }) =>
// 		iconColor &&
// 		css`
// 			> svg {
// 				path {
// 					fill: ${iconColor};
// 				}
// 			}
// 		`};

// 	${({ big }) =>
// 		big &&
// 		css`
// 			right: 5px;
// 			bottom: 5px;
// 			padding: 5px 10px;
// 			span {
// 				margin-left: 10px;
// 			}
// 			@media ${mobile} {
// 				display: none;
// 			}
// 		`}
// `;

const ProfilePictureWrapper = styled.div`
	position: relative;
	${row(0, "space-between", "flex-end")};
`;

const Relative = styled.div`
	position: relative;
`;

const rightButtonStyle = css`
	@media ${desktop} {
		align-self: flex-end;
		//margin-bottom: -11px;
	}
`;

const StyledInvitationButton = styled(InvitationButton)`
	${rightButtonStyle};
	font-size: 0.8125rem;
	font-weight: 700;
	cursor: pointer;
	border: none;
	outline: none;
	border-radius: 6px;
	padding: 6px 14px;
	line-height: 15px;
	display: -webkit-box;
	display: -webkit-flex;
	display: -ms-flexbox;
	display: flex;
	-webkit-align-items: center;
	-webkit-box-align: center;
	-ms-flex-align: center;
	align-items: center;
	-webkit-box-pack: center;
	-webkit-justify-content: center;
	-ms-flex-pack: center;
	justify-content: center;
	background-color: ${({ theme }) => (Environment.APP_VARIANT === "jaangal" ? theme.colors.primaryLight : "#FFDCDC")};
	color: ${({ theme }) => (Environment.APP_VARIANT === "jaangal" ? theme.colors.primary : "#E83636")};
	-webkit-transition: background-color 0.2s linear;
	transition: background-color 0.2s linear;
`;

const BottomInvitationButton = styled(InvitationButton)`
	margin: 30px auto 0;
`;
